import React, { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { SQUAD_TYPOGRAPHY } from './pageHeaderTokens';
import {
  SQUAD_BAR_HEIGHT_PX,
  SQUAD_BAR_LABEL_BAR_GAP_PX,
  SQUAD_BAR_LABEL_COLUMN_FALLBACK_PX,
  SQUAD_BAR_RIGHT_MARGIN_PX,
  SQUAD_PORTFOLIO_FONT_FAMILY,
  SQUAD_PORTFOLIO_TEXT_COLOR,
  formatPortfolioPercent,
  shouldShowBarValueInside,
  squadBarChartLabelText,
} from './squadPortfolioChartUtils';

const REFERENCE_LABELS = ['Nachhaltige Ernährung & Konsum', 'Arbitrage u. Übernahmeangebote'] as const;

type Props = {
  items: Array<{ label: string; value: number }>;
  /** Primary brand color — all bars use index 0 (widget convention). */
  barColor: string;
  fontSize?: number;
};

export const SquadPortfolioBarChart: FC<Props> = ({
  items,
  barColor,
  fontSize = SQUAD_TYPOGRAPHY.bar,
}) => {
  const measureRef = useRef<HTMLSpanElement>(null);
  const [labelColumnWidthPx, setLabelColumnWidthPx] = useState(SQUAD_BAR_LABEL_COLUMN_FALLBACK_PX);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    let max = 0;
    for (const text of REFERENCE_LABELS) {
      el.textContent = text;
      max = Math.max(max, el.offsetWidth);
    }
    if (max > 0) setLabelColumnWidthPx(Math.min(max, SQUAD_BAR_LABEL_COLUMN_FALLBACK_PX));
  }, [fontSize]);

  const peak = useMemo(
    () => Math.max(0, ...(items || []).map((i) => Number(i.value) || 0), 0) || 1,
    [items]
  );

  const rows = useMemo(
    () =>
      (items || []).map((item) => {
        const value = Number(item.value) || 0;
        const pct = Math.max(0, Math.min(100, (value / peak) * 100));
        return {
          label: squadBarChartLabelText(item.label),
          value,
          pct,
          formatted: formatPortfolioPercent(value),
        };
      }),
    [items, peak]
  );

  return (
    <Box
      lang="de"
      sx={{
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        fontFamily: SQUAD_PORTFOLIO_FONT_FAMILY,
        color: SQUAD_PORTFOLIO_TEXT_COLOR,
        fontSize,
        pr: `${SQUAD_BAR_RIGHT_MARGIN_PX}px`,
        position: 'relative',
        boxSizing: 'border-box',
      }}
    >
      <Box
        component="span"
        ref={measureRef}
        aria-hidden
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          fontFamily: SQUAD_PORTFOLIO_FONT_FAMILY,
          fontSize,
          pointerEvents: 'none',
          left: 0,
          top: 0,
        }}
      />
      {rows.map((row, idx) => {
        const valueInside = shouldShowBarValueInside(row.pct, row.formatted);
        return (
          <Box
            key={idx}
            sx={{
              display: 'grid',
              gridTemplateColumns: `${labelColumnWidthPx}px minmax(0, 1fr)`,
              columnGap: `${SQUAD_BAR_LABEL_BAR_GAP_PX}px`,
              alignItems: 'center',
              minHeight: 24,
              py: '3px',
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <Box
              title={row.label}
              sx={{
                textAlign: 'right',
                lineHeight: 1.25,
                whiteSpace: 'pre-line',
                width: labelColumnWidthPx,
                maxWidth: labelColumnWidthPx,
                minWidth: 0,
                justifySelf: 'end',
              }}
            >
              {row.label}
            </Box>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: SQUAD_BAR_HEIGHT_PX,
                display: 'flex',
                alignItems: 'center',
                overflow: 'visible',
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: `${row.pct}%`,
                  flexShrink: 0,
                  height: '100%',
                  backgroundColor: barColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pr: valueInside ? 1 : 0,
                  pl: valueInside ? 0.5 : 0,
                  boxSizing: 'border-box',
                  // '1px', not 1: MUI reads numbers up to 1 as a fraction, so 1 was 100 % and every
                  // bar whose value sits outside filled the whole track (value cut off at the edge).
                  minWidth: valueInside ? undefined : row.value > 0 ? '1px' : 0,
                  overflow: valueInside ? 'hidden' : undefined,
                }}
              >
                {valueInside ? (
                  <Box component="span" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {row.formatted}
                  </Box>
                ) : null}
              </Box>
              {!valueInside ? (
                <Box component="span" sx={{ ml: 1, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {row.formatted}
                </Box>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
